import { useState } from 'react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from './ui/sheet';
import { Separator } from './ui/separator';
import { useIsMobile } from './ui/use-mobile';
import { 
  Archive, 
  User, 
  LogOut,
  Settings,
  Menu,
  Shield,
  Building,
  ChevronRight,
  IdCard
} from 'lucide-react';
import { ProfileManager } from './ProfileManager';
import { NotificationBell } from './NotificationBell';
import { User as UserType } from '../types';

interface NavigationProps {
  user: UserType;
  onLogout: () => void;
  onUpdateProfile: (updatedUser: UserType) => void;
}

export function Navigation({ user, onLogout, onUpdateProfile }: NavigationProps) {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'administrator':
        return 'Administrator';
      case 'student':
        return 'Student';
      case 'faculty':
        return 'Faculty';
      default:
        return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'administrator':
        return { bg: 'from-red-500 to-rose-600', ring: 'ring-red-200', badgeBg: 'bg-red-100 text-red-700 border-red-200', avatarBg: 'from-red-500 to-rose-600' };
      case 'faculty':
        return { bg: 'from-blue-500 to-indigo-600', ring: 'ring-blue-200', badgeBg: 'bg-blue-100 text-blue-700 border-blue-200', avatarBg: 'from-blue-500 to-indigo-600' };
      case 'student':
        return { bg: 'from-emerald-500 to-teal-600', ring: 'ring-emerald-200', badgeBg: 'bg-emerald-100 text-emerald-700 border-emerald-200', avatarBg: 'from-emerald-500 to-teal-600' };
      default:
        return { bg: 'from-gray-500 to-slate-600', ring: 'ring-gray-200', badgeBg: 'bg-gray-100 text-gray-700 border-gray-200', avatarBg: 'from-gray-500 to-slate-600' };
    }
  };

  const roleColors = getRoleColor(user.role);

  if (isMobile) {
    return (
      <>
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:bg-blue-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg">
          Skip to main content
        </a>
        <header className="border-b bg-white/90 backdrop-blur-lg sticky top-0 z-50 shadow-sm" role="banner">
          <nav aria-label="Main navigation" className="flex h-14 items-center justify-between px-3 gap-2">
            {/* Logo - compact on mobile */}
            <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md shadow-blue-600/20">
                <Archive className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm font-bold bg-gradient-to-r from-blue-700 to-indigo-600 bg-clip-text text-transparent leading-tight">AIRS</h1>
              </div>
            </div>

            {/* User summary + menu trigger on mobile */}
            <div className="flex items-center gap-2 min-w-0">
              <NotificationBell />
              <div className="hidden xs:flex items-center gap-1.5 min-w-0">
                <span className="text-xs font-medium text-foreground truncate max-w-[100px]">{user.name.split(' ')[0]}</span>
                <Badge className={`text-[10px] px-1.5 py-0 h-4 font-semibold border ${roleColors.badgeBg} flex-shrink-0`}>
                  {getRoleDisplayName(user.role).slice(0, 5)}
                </Badge>
              </div>
              
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="flex-shrink-0 h-8 w-8 p-0 shadow-sm hover:shadow-md transition-all border-slate-200">
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[min(320px,85vw)] p-0">
                  <SheetHeader className="px-5 pt-5 pb-0">
                    <SheetTitle className="text-left text-base">Account</SheetTitle>
                    <SheetDescription className="text-left text-xs">
                      Manage your profile and settings
                    </SheetDescription>
                  </SheetHeader>
                  
                  <div className="px-5 py-4">
                    {/* User card in mobile menu */}
                    <div className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${roleColors.bg} p-4`}>
                      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Ccircle%20cx%3D%221%22%20cy%3D%221%22%20r%3D%221%22%20fill%3D%22rgba(255%2C255%2C255%2C0.08)%22/%3E%3C/svg%3E')] opacity-60" />
                      <div className="relative flex items-center gap-3">
                        <Avatar className="h-12 w-12 flex-shrink-0 ring-2 ring-white/30 shadow-lg">
                          {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
                          <AvatarFallback className={`text-sm font-bold bg-white/20 text-white backdrop-blur-sm`}>
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-white text-sm truncate">{user.name}</p>
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/20 text-white backdrop-blur-sm border border-white/20">
                              <Shield className="w-2.5 h-2.5" />
                              {getRoleDisplayName(user.role)}
                            </span>
                          </div>
                          <p className="text-[11px] text-white/70 mt-1 truncate">{user.email}</p>
                        </div>
                      </div>
                    </div>

                    {/* Quick info items */}
                    <div className="mt-3 space-y-1">
                      <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-muted-foreground">
                        <IdCard className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">ID: {user.idNumber}</span>
                      </div>
                      {user.department && (
                        <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-muted-foreground">
                          <Building className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{user.department}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="px-5 py-4 space-y-1.5">
                    <Dialog open={isProfileOpen} onOpenChange={setIsProfileOpen}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" className="w-full justify-between h-11 px-3 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-all group">
                          <span className="flex items-center gap-2.5">
                            <Settings className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                            <span className="text-sm font-medium">Profile Settings</span>
                          </span>
                          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="w-[96vw] max-w-[96vw] sm:max-w-2xl max-h-[92vh] overflow-y-auto p-0 gap-0">
                        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-0 pr-12">
                          <DialogTitle className="text-base">My Profile</DialogTitle>
                          <DialogDescription className="text-xs">
                            Update your personal information and account settings
                          </DialogDescription>
                        </DialogHeader>
                        <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                          <ProfileManager 
                            user={user} 
                            onUpdateProfile={(updatedUser) => {
                              onUpdateProfile(updatedUser);
                              setIsProfileOpen(false);
                              setIsMobileMenuOpen(false);
                            }} 
                          />
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    <Button 
                      variant="ghost" 
                      className="w-full justify-between h-11 px-3 hover:bg-red-50 hover:text-red-700 rounded-lg transition-all group" 
                      onClick={() => {
                        onLogout();
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <span className="flex items-center gap-2.5">
                        <LogOut className="h-4 w-4 text-muted-foreground group-hover:text-red-600 transition-colors" />
                        <span className="text-sm font-medium">Logout</span>
                      </span>
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </nav>
        </header>
      </>
    );
  }

  return (
    <>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:bg-blue-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg">
        Skip to main content
      </a>
      <header className="border-b bg-white/90 backdrop-blur-lg sticky top-0 z-50 shadow-sm" role="banner">
        <nav aria-label="Main navigation" className="flex h-16 items-center justify-between px-4 lg:px-6 gap-4">
          {/* Logo */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20">
              <Archive className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold bg-gradient-to-r from-blue-700 to-indigo-600 bg-clip-text text-transparent leading-tight">AIRS</h1>
              <p className="text-[10px] text-muted-foreground leading-tight hidden md:block">
                Archive Intelligent Room Scheduler
              </p>
            </div>
          </div>
          
          {/* Right section */}
          <div className="flex items-center gap-3 min-w-0">
            {/* User info - responsive */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="text-right min-w-0 hidden sm:block">
                <p className="text-sm font-semibold truncate max-w-[180px] lg:max-w-xs">{user.name}</p>
                <div className="flex items-center justify-end gap-1.5 mt-0.5">
                  <Badge className={`text-[10px] px-1.5 py-0 h-4 font-semibold border ${roleColors.badgeBg} flex-shrink-0`}>
                    {getRoleDisplayName(user.role)}
                  </Badge>
                  <span className="text-[11px] text-muted-foreground truncate hidden lg:inline">
                    {user.idNumber}
                  </span>
                </div>
                {user.department && (
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5 max-w-[180px] hidden lg:block">
                    {user.department}
                  </p>
                )}
              </div>
              <Avatar className={`h-9 w-9 lg:h-10 lg:w-10 flex-shrink-0 ring-2 ${roleColors.ring} hover:ring-offset-1 transition-all cursor-pointer`}>
                {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
                <AvatarFallback className={`text-xs lg:text-sm bg-gradient-to-br ${roleColors.avatarBg} text-white font-bold`}>
                  {getInitials(user.name)}
                </AvatarFallback>
              </Avatar>
            </div>
            
            {/* Actions */}
            <Separator orientation="vertical" className="h-8 hidden sm:block" />
            <div className="flex items-center gap-1.5">
              <NotificationBell />
              <Dialog open={isProfileOpen} onOpenChange={setIsProfileOpen}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-8 lg:h-9 px-2.5 lg:px-3 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-all shadow-sm text-xs lg:text-sm"
                  >
                    <Settings className="h-3.5 w-3.5 lg:mr-1.5" />
                    <span className="hidden lg:inline">Profile</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="w-[96vw] max-w-[96vw] sm:max-w-2xl lg:max-w-3xl max-h-[92vh] overflow-y-auto p-0 gap-0">
                  <DialogHeader className="px-5 sm:px-6 pt-5 sm:pt-6 pb-0 pr-12">
                    <DialogTitle>My Profile</DialogTitle>
                    <DialogDescription>
                      Update your personal information and account settings
                    </DialogDescription>
                  </DialogHeader>
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6">
                    <ProfileManager 
                      user={user} 
                      onUpdateProfile={(updatedUser) => {
                        onUpdateProfile(updatedUser);
                        setIsProfileOpen(false);
                      }} 
                    />
                  </div>
                </DialogContent>
              </Dialog>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onLogout} 
                className="h-8 lg:h-9 px-2.5 lg:px-3 hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-all shadow-sm text-xs lg:text-sm"
              >
                <LogOut className="h-3.5 w-3.5 lg:mr-1.5" />
                <span className="hidden lg:inline">Logout</span>
              </Button>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
}